import React, { useId } from 'react';
import { Crosshair, Maximize2, Minus, Plus } from 'lucide-react';
import type { MapLabel, MapPoint, PlaybackMarker } from '../../data/itinerary-mock';

const VIEW_W = 100;
const VIEW_H = 70;

const ROADS = [
  'M 0 16 H 100',
  'M 0 34 H 100',
  'M 0 52 H 100',
  'M 18 0 V 70',
  'M 40 0 V 70',
  'M 63 0 V 70',
  'M 84 0 V 70',
  'M 4 64 C 22 50, 36 46, 52 36 S 78 20, 98 8',
];

const LANES = [
  'M 8 22 H 92',
  'M 8 44 H 94',
  'M 28 4 V 66',
  'M 72 6 V 64',
];

const BLOCKS: Array<[number, number, number, number]> = [
  [4, 4, 12, 8],
  [22, 6, 14, 7],
  [44, 4, 14, 8],
  [68, 5, 12, 7],
  [6, 20, 10, 8],
  [24, 22, 13, 7],
  [46, 20, 12, 8],
  [70, 22, 14, 7],
  [5, 38, 11, 8],
  [26, 40, 12, 7],
  [48, 38, 13, 8],
  [72, 40, 12, 7],
  [8, 56, 12, 8],
  [30, 58, 14, 7],
  [54, 56, 11, 8],
];

function toPath(points: MapPoint[]): string {
  return points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`)
    .join(' ');
}

function pointAlong(points: MapPoint[], progress: number): MapPoint {
  if (points.length === 0) return { x: 0, y: 0 };
  if (points.length === 1 || progress <= 0) return points[0];
  if (progress >= 1) return points[points.length - 1];
  const lengths: number[] = [];
  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    const length = Math.hypot(points[index].x - points[index - 1].x, points[index].y - points[index - 1].y);
    lengths.push(length);
    total += length;
  }
  let left = progress * total;
  for (let index = 0; index < lengths.length; index += 1) {
    if (left <= lengths[index] || index === lengths.length - 1) {
      const span = lengths[index] || 1;
      const blend = Math.min(1, left / span);
      return {
        x: points[index].x + (points[index + 1].x - points[index].x) * blend,
        y: points[index].y + (points[index + 1].y - points[index].y) * blend,
      };
    }
    left -= lengths[index];
  }
  return points[points.length - 1];
}

export interface ItineraryMapProps {
  name: string;
  path: MapPoint[];
  labels: MapLabel[];
  color: string;
  weight: number;
  zoom: number;
  marker: PlaybackMarker;
  activeFrom: number | null;
  activeTo: number | null;
  compact?: boolean;
  testId?: string;
  onZoom?: (zoom: number) => void;
  onExpand?: () => void;
  onReset?: () => void;
}

const ItineraryMap: React.FC<ItineraryMapProps> = ({
  name,
  path,
  labels,
  color,
  weight,
  zoom,
  marker,
  activeFrom,
  activeTo,
  compact = false,
  testId = 'itinerary-map',
  onZoom,
  onExpand,
  onReset,
}) => {
  const uid = useId().replace(/:/g, '');
  const frame = `translate(${VIEW_W / 2} ${VIEW_H / 2}) scale(${zoom}) translate(${-VIEW_W / 2} ${-VIEW_H / 2})`;
  const route = toPath(path);
  const active = activeFrom != null && activeTo != null
    ? toPath(path.slice(activeFrom, activeTo + 1))
    : '';
  const start = path[0];
  const end = path[path.length - 1];
  const head = pointAlong(path, 0.62);

  return (
    <div className={`it-map${compact ? ' is-compact' : ''}`} data-testid={testId}>
      <p className="visually-hidden">{`Illustrated map for ${name}.`}</p>
      <svg className="it-map__svg" viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} role="img" aria-label={`${name} route map`}>
        <defs>
          <filter id={`${uid}-shadow`} x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="0" dy="0.4" stdDeviation="0.6" floodOpacity="0.28" />
          </filter>
        </defs>
        <g transform={frame}>
          <rect className="it-map__paper" width={VIEW_W} height={VIEW_H} />
          {BLOCKS.map(([x, y, blockWidth, blockHeight]) => (
            <rect key={`${x}-${y}`} className="it-map__block" x={x} y={y} width={blockWidth} height={blockHeight} rx="1.2" />
          ))}
          <ellipse className="it-map__park" cx="76" cy="50" rx="12" ry="7" />
          <path className="it-map__water" d="M92 0c-4 8 4 12 1 20s3 8-1 16 4 10 1 18 2 10 8 16V0z" />
          <g className="it-map__roads">
            {ROADS.map((d) => <path key={d} d={d} />)}
          </g>
          <g className="it-map__lanes">
            {LANES.map((d) => <path key={d} d={d} />)}
          </g>
          {!compact && labels.map((label) => (
            <text key={label.text} x={label.x} y={label.y} textAnchor="middle" className="it-map__label">
              {label.text}
              {label.sub ? <tspan x={label.x} dy="3.2" className="it-map__sub">{label.sub}</tspan> : null}
            </text>
          ))}
          {route ? (
            <path className="it-map__halo" d={route} strokeWidth={weight + 1.4} />
          ) : null}
          {route ? (
            <path
              className="it-map__route"
              d={route}
              stroke={color}
              strokeWidth={weight}
              opacity={active ? 0.35 : 1}
            />
          ) : null}
          {active ? (
            <path className="it-map__route" d={active} stroke={color} strokeWidth={weight + 0.6} />
          ) : null}
          {start ? (
            <g transform={`translate(${start.x} ${start.y})`} filter={`url(#${uid}-shadow)`}>
              <circle className="it-map__pin" r="3.3" />
              <text className="it-map__pin-num" y="1.15" textAnchor="middle">1</text>
            </g>
          ) : null}
          {end && path.length > 1 ? (
            <g transform={`translate(${end.x} ${end.y})`} filter={`url(#${uid}-shadow)`}>
              <circle className="it-map__pin" r="3.3" />
              <text className="it-map__pin-num" y="1.15" textAnchor="middle">2</text>
            </g>
          ) : null}
          <g transform={`translate(${head.x} ${head.y})`} className={`it-map__marker it-map__marker--${marker}`}>
            {marker === 'car' ? (
              <rect x="-3.2" y="-1.8" width="6.4" height="3.6" rx="1.1" />
            ) : null}
            {marker === 'waze' ? <polygon points="0,-3.4 3.2,2.6 -3.2,2.6" /> : null}
            {marker === 'maps' ? (
              <path d="M0-4.2c-1.7 0-3 1.3-3 3 0 2.2 3 5.2 3 5.2s3-3 3-5.2c0-1.7-1.3-3-3-3z" />
            ) : null}
          </g>
        </g>
      </svg>
      {!compact && (onZoom || onExpand || onReset) ? (
        <div className="it-map__tools">
          {onExpand ? (
            <button type="button" aria-label="Expand map" onClick={onExpand}>
              <Maximize2 size={16} aria-hidden="true" />
            </button>
          ) : null}
          {onReset ? (
            <button type="button" aria-label="Reset map view" onClick={onReset}>
              <Crosshair size={16} aria-hidden="true" />
            </button>
          ) : null}
          {onZoom ? (
            <button type="button" aria-label="Zoom in" onClick={() => onZoom(Math.min(2.2, Number((zoom + 0.2).toFixed(2))))} disabled={zoom >= 2.2}>
              <Plus size={16} aria-hidden="true" />
            </button>
          ) : null}
          {onZoom ? (
            <button type="button" aria-label="Zoom out" onClick={() => onZoom(Math.max(1, Number((zoom - 0.2).toFixed(2))))} disabled={zoom <= 1}>
              <Minus size={16} aria-hidden="true" />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default ItineraryMap;
